import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface ConferencePackage {
    package_id: number;
    period_id: number;
    service_id: number;
    code: string | null;
    name: string;
    description: string | null;
    photo_path: string | null;
    is_presenter: boolean | number;
    participant_type_target: "general" | "student_s1_s2" | "student_s3";
    tier_name: "early_bird" | "regular";
    start_date: string;
    end_date: string;
    currency: "IDR" | "USD";
    price: string | number;
    is_active: boolean | number;
    created_at?: string;
    updated_at?: string;
}

export type ConferencePackagePayload = Omit<ConferencePackage, "package_id" | "photo_path" | "created_at" | "updated_at">;

export const useAdminConferencePackages = (periodId?: string) => {
    return useQuery<ConferencePackage[]>({
        queryKey: ["admin-conference-packages", periodId],
        queryFn: async () => {
            const params = periodId ? { period_id: periodId } : {};
            const { data } = await axiosClient.get("/conference-packages", { params, withCredentials: true });
            return data;
        },
    });
};

export const useCreateConferencePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ConferencePackagePayload) => {
            const { data } = await axiosClient.post("/conference-packages", payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-conference-packages"] }),
    });
};

export const useUpdateConferencePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<ConferencePackagePayload> }) => {
            const { data } = await axiosClient.put(`/conference-packages/${id}`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-conference-packages"] }),
    });
};

export const useDeleteConferencePackage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/conference-packages/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-conference-packages"] }),
    });
};
